import { Fragment } from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  Button,
  Divider,
  StackLayout,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { SectionCard } from 'components/section-card/section-card.component';
import { LeaveWithoutSavingModal } from 'components/modals/leave-without-saving-modal/leave-without-saving-modal.component';
import {
  CHANNEL_ITEMS,
  DOCUMENT_DELIVERY_ITEMS,
  type CommunicationPreferencesValues,
  type ToggleGroupKey,
  type ToggleItemConfig,
} from './communication-preferences.types';
import { useCommunicationPreferencesForm } from './use-communication-preferences-form.hook';
import { ContactInformationSection } from './contact-information-section.component';
import { ToggleGroup } from './toggle-group.component';
import { DisclaimerBanner } from './disclaimer-banner.component';

export const CommunicationPreferencesForm = ({
  initialValues,
  onSave,
  isSaving = false,
}: {
  initialValues?: CommunicationPreferencesValues;
  onSave?: (
    values: CommunicationPreferencesValues,
    seededValues: CommunicationPreferencesValues,
  ) => void;
  isSaving?: boolean;
} = {}) => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useMediaQuery(queryHelpers.down('tablet'));
  const {
    contact,
    setContactField,
    groups,
    toggleMaster,
    toggleItem,
    errors,
    isValid,
    isDirty,
    handleSave,
  } = useCommunicationPreferencesForm({ initialValues, onSave });

  const toggleGroups: {
    key: ToggleGroupKey;
    heading: string;
    description?: string;
    masterLabel: string;
    masterCaption: string;
    items: ToggleItemConfig[];
  }[] = [
    {
      key: 'documentDelivery',
      heading: formatMessage({ id: 'STR_COMM_PREF_DOCUMENT_DELIVERY' }),
      description: formatMessage({
        id: 'STR_COMM_PREF_DOCUMENT_DELIVERY_DESCRIPTION',
      }),
      masterLabel: formatMessage({ id: 'STR_COMM_PREF_GO_PAPERLESS' }),
      masterCaption: formatMessage({ id: 'STR_COMM_PREF_GO_PAPERLESS_HINT' }),
      items: DOCUMENT_DELIVERY_ITEMS,
    },
    {
      key: 'careWellness',
      heading: formatMessage({ id: 'STR_COMM_PREF_CARE_WELLNESS' }),
      masterLabel: formatMessage({ id: 'STR_COMM_PREF_RECEIVE_UPDATES' }),
      masterCaption: formatMessage({
        id: 'STR_COMM_PREF_CARE_WELLNESS_HINT',
      }),
      items: CHANNEL_ITEMS,
    },
    {
      key: 'promotions',
      heading: formatMessage({ id: 'STR_COMM_PREF_PROMOTIONS' }),
      masterLabel: formatMessage({ id: 'STR_COMM_PREF_RECEIVE_UPDATES' }),
      masterCaption: formatMessage({ id: 'STR_COMM_PREF_PROMOTIONS_HINT' }),
      items: CHANNEL_ITEMS,
    },
  ];

  return (
    <SectionCard>
      <StackLayout spacing="$two" horizontalAlignment="stretch">
        <ContactInformationSection
          contact={contact}
          errors={errors}
          onChange={setContactField}
        />

        {toggleGroups.map((toggleGroup) => (
          <Fragment key={toggleGroup.key}>
            <Divider />
            <ToggleGroup
              heading={toggleGroup.heading}
              description={toggleGroup.description}
              masterId={`comm-pref-${toggleGroup.key}`}
              masterLabel={toggleGroup.masterLabel}
              masterCaption={toggleGroup.masterCaption}
              items={toggleGroup.items}
              group={groups[toggleGroup.key]}
              onToggleMaster={() => toggleMaster(toggleGroup.key)}
              onToggleItem={(itemId) => toggleItem(toggleGroup.key, itemId)}
            />
            {/* The mail disclaimer sits inside the document-delivery group. */}
            {toggleGroup.key === 'documentDelivery' && <DisclaimerBanner />}
          </Fragment>
        ))}

        <Box
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Button
            priority="primary"
            onClick={handleSave}
            disabled={!isValid || !isDirty || isSaving}
            width={isBelowTablet ? 'fillContainer' : 'hugContents'}
          >
            {formatMessage({ id: 'STR_SAVE' })}
          </Button>
        </Box>
      </StackLayout>
      <LeaveWithoutSavingModal when={isDirty} />
    </SectionCard>
  );
};
